'use client';

import React, { useEffect, useRef } from 'react';
import { Home, TrendingUp, Building2, Landmark, BarChart3, Crown } from 'lucide-react';

const audiences = [
  {
    icon: Home,
    title: 'Aspiring Property Practitioners',
    desc: 'You want to enter the real estate industry but are not sure where to start. REUSA gives you the foundation, the credentials and the confidence to begin your career the right way.',
    cta: 'Start your journey',
  },
  {
    icon: TrendingUp,
    title: 'Estate Agents',
    desc: 'You are already in the industry and want to grow. Whether you are building your client base, specialising in a niche, or preparing to open your own agency — REUSA helps you get there faster.',
    cta: 'Grow your practice',
  },
  {
    icon: Building2,
    title: 'Property Managers',
    desc: 'Managing properties is a profession that deserves proper education. REUSA equips you with the operational, legal and financial knowledge to manage portfolios with confidence.',
    cta: 'Advance your skills',
  },
  {
    icon: Landmark,
    title: 'Developers',
    desc: 'From feasibility to completion, property development demands a broad skill set. REUSA offers the strategic and practical education to help you develop with greater certainty and less risk.',
    cta: 'Build with knowledge',
  },
  {
    icon: BarChart3,
    title: 'Investors',
    desc: 'Whether you are building your first portfolio or managing a significant asset base, REUSA helps you make smarter investment decisions grounded in real market knowledge.',
    cta: 'Invest with insight',
  },
  {
    icon: Crown,
    title: 'Industry Leaders',
    desc: 'You have built a career. Now you want to shape the industry. REUSA\'s executive education is designed for the professionals who are ready to lead, govern and influence the future of African real estate.',
    cta: 'Lead the industry',
  },
];

export default function AudienceSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const els = ref?.current?.querySelectorAll('.section-reveal, .stagger-child');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-24 bg-secondary relative overflow-hidden" ref={ref}>
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-border" />
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        {/* Header */}
        <div className="text-center mb-16 section-reveal">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-accent/60" />
            <span className="font-sans text-[0.65rem] font-semibold tracking-widest uppercase text-accent/80">Who We Serve</span>
            <div className="h-px w-12 bg-accent/60" />
          </div>
          <h2 className="font-serif text-foreground mb-5" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', lineHeight: 1.15, letterSpacing: '-0.015em' }}>
            Built For Every Stage Of Your<br />
            <span className="italic text-gradient-gold">Property Journey</span>
          </h2>
          <p className="font-sans text-muted-foreground max-w-xl mx-auto leading-relaxed text-sm">
            Whether you are just starting out or ready to lead, REUSA has a clear pathway designed for where you are right now — and where you want to go.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {audiences?.map((audience, i) => (
            <div
              key={audience?.title}
              className="stagger-child bg-card border border-border rounded-2xl p-7 group hover:border-accent/40 hover:shadow-lg transition-all duration-300"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-secondary border border-border flex items-center justify-center mb-5 group-hover:bg-primary group-hover:border-primary transition-all duration-300">
                <audience.icon size={22} className="text-primary group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-serif font-semibold text-foreground text-lg mb-3 leading-tight">{audience?.title}</h3>
              <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-5">{audience?.desc}</p>
              <span className="font-sans text-xs font-semibold tracking-widest uppercase text-accent/70 group-hover:text-accent transition-colors" style={{ letterSpacing: '0.1em' }}>
                {audience?.cta} →
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
